import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import NodeAllPropsDropdown from "../components/NodeAllPropsDropdown";
import Interaction from "../../../models/interactions/Interaction";
import {
  IInspectProp,
  requiredInspectProperties,
} from "../../../models/interactions/InspectProp";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeStringDropdown from "../components/NodeStringDropdown";
import usePropHelper from "../../../hooks/PropHelper";
import { selectUsedProps } from "../../../app/slices/PropLibrarySlice";
import NodeToggle from "../components/NodeToggle";
import NodeNumberField from "../components/NodeNumberField";
import { UpdatePropertyOfInteraction } from "../../../app/slices/helpers/TrainingSliceHelper";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function InspectPropNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const propInteraction = props.interaction as IInspectProp;
  const { tryGetUsedPropByInstanceId } = usePropHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();
  const usedProps = useSelector(selectUsedProps);
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const usedProp = useMemo(() => {
    if (!propInteraction.propId) return null;
    return tryGetUsedPropByInstanceId(propInteraction.propId);
  }, [propInteraction.propId, usedProps, tryGetUsedPropByInstanceId]);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const propDropdown = useMemo(
    () => (
      <NodeAllPropsDropdown
        label="Prop"
        selectedPropStateId={propInteraction.propId}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-prop-on-map-button"
            className={
              NODE_PICK_BUTTON_BASE +
              (isPickerActiveForNode
                ? NODE_PICK_BUTTON_ACTIVE
                : NODE_PICK_BUTTON_IDLE)
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "prop",
                  title: "Pick prop",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
        onClick={(propStateId) => {
          const prop = { id: propStateId } as any;
          updateInteraction(props.nodeId, {
            ...applyPickedPropToInteraction(
              propInteraction,
              prop,
              tryGetUsedPropByInstanceId,
            ),
          } as IInspectProp);
        }}
      />
    ),
    [
      dispatch,
      tryGetUsedPropByInstanceId,
      isPickerActiveForNode,
      propInteraction,
      props.nodeId,
      updateInteraction,
    ],
  );

  const propStateDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={usedProp?.metadata.inspectDefinitions.map((p) => p.name) ?? []}
        selectedItem={propInteraction.tag}
        label="Tag"
        onClick={(tag) => {
          updateInteraction(props.nodeId, {
            ...propInteraction,
            tag: tag,
          } as IInspectProp);
        }}
      />
    ),
    [usedProp, propInteraction.propId, updateInteraction, props.nodeId],
  );

  const instantProperty = useMemo(() => {
    return propInteraction.properties?.find((p) => p.id === "instant");
  }, [propInteraction.properties]);
  const speedProperty = useMemo(() => {
    return propInteraction.properties?.find((p) => p.id === "speed");
  }, [propInteraction.properties]);
  const isInstant = (instantProperty?.value as boolean) ?? false;

  const instantToggle = useMemo(
    () => (
      <NodeToggle
        label="Instant"
        value={isInstant}
        onChange={(instant) => {
          if (instantProperty) {
            const updatedProperty = { ...instantProperty, value: instant };
            const updatedInteraction = UpdatePropertyOfInteraction(
              propInteraction,
              updatedProperty,
            );
            updateInteraction(props.nodeId, updatedInteraction);
          }
        }}
      />
    ),
    [instantProperty, isInstant, propInteraction, updateInteraction, props.nodeId],
  );

  const speedInput = useMemo(
    () => (
      <NodeNumberField
        label="Speed"
        value={(speedProperty?.value as number) ?? 1}
        disabled={isInstant}
        onChange={(speed) => {
          const baseProperty =
            speedProperty ??
            requiredInspectProperties.find((property) => property.id === "speed");
          if (!baseProperty) return;

          const updatedProperty = {
            ...baseProperty,
            value: speed,
          };

          const updatedInteraction = speedProperty
            ? UpdatePropertyOfInteraction(propInteraction, updatedProperty)
            : ({
                ...propInteraction,
                properties: [...(propInteraction.properties ?? []), updatedProperty],
              } as IInspectProp);

          updateInteraction(props.nodeId, updatedInteraction);
        }}
      />
    ),
    [isInstant, propInteraction, props.nodeId, speedProperty, updateInteraction],
  );

  return (
    <div className="flex flex-col w-full h-full gap-[12px] noDrag">
      {propDropdown}
      {propStateDropdown}
      {instantToggle}
      {speedInput}
    </div>
  );
}
